export interface SelectableRun {
  id: string;
  elements: readonly { key: string }[];
}

export interface RunClickModifiers {
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
}

export interface RunSelectionResult {
  selection: Set<string>;
  anchor: string | undefined;
}

export type RunClickTarget = string | readonly string[];

function targetRuns(orderedRuns: readonly SelectableRun[], clicked: RunClickTarget): SelectableRun[] {
  const ids = new Set(typeof clicked === 'string' ? [clicked] : clicked);
  return orderedRuns.filter((run) => ids.has(run.id));
}

function runKeys(runs: readonly SelectableRun[]): Set<string> {
  return new Set(runs.flatMap((run) => run.elements.map((element) => element.key)));
}

/** Apply one run-row or zone-heading click using the room's visual run order. */
export function applyRunClick(
  current: ReadonlySet<string>,
  anchor: string | undefined,
  orderedRuns: readonly SelectableRun[],
  clicked: RunClickTarget,
  modifiers: RunClickModifiers,
): RunSelectionResult {
  const runs = targetRuns(orderedRuns, clicked);
  const clickedAnchor = runs[0]?.id;
  if (!clickedAnchor) return { selection: new Set(current), anchor };

  if (modifiers.shiftKey) {
    const anchorIndex = anchor == null ? -1 : orderedRuns.findIndex((run) => run.id === anchor);
    if (anchorIndex < 0) return { selection: runKeys(runs), anchor: clickedAnchor };
    const firstClicked = orderedRuns.findIndex((run) => run.id === runs[0].id);
    const lastClicked = orderedRuns.findIndex((run) => run.id === runs.at(-1)!.id);
    const first = Math.min(anchorIndex, firstClicked);
    const last = Math.max(anchorIndex, lastClicked);
    return { selection: new Set([...current, ...runKeys(orderedRuns.slice(first, last + 1))]), anchor };
  }

  if (modifiers.metaKey || modifiers.ctrlKey) {
    const target = runKeys(runs);
    const selection = new Set(current);
    const allSelected = [...target].every((key) => selection.has(key));
    for (const key of target) {
      if (allSelected) selection.delete(key);
      else selection.add(key);
    }
    return { selection, anchor: clickedAnchor };
  }

  return { selection: runKeys(runs), anchor: clickedAnchor };
}

/** Explicit scene targets are sent element-by-element; no selection delegates whole-room capture to the daemon. */
export function selectionForSceneSave(current: ReadonlySet<string>): Set<string> | undefined {
  return current.size ? new Set(current) : undefined;
}
